/**
 * The assistant screen's route and its campaign-nav entry, discovered by
 * `../routes.ts`'s glob.
 *
 * `AssistantScope`'s window and account selection are not this task's call
 * (plan issue 9): no picker exists yet, so the route hands `Assistant` the
 * unrestricted default -- `window: null`, read by AS-1's loop as "no window
 * argument" -- and fixed labels naming that default, not a per-request
 * selection. A picker replaces both when it lands.
 *
 * `?post=` scopes the screen to one post (73-2-07's 'Perguntar sobre esta
 * publicação'): the route reads it, resolves it through the same `usePost`
 * the publication screen uses, and hands `Assistant` either a chip label or
 * `unknownPost` -- an id this organisation's collection has no post for opens
 * unscoped rather than failing the route.
 */

import { createElement, type ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { useNavigate, useParams } from '@tanstack/react-router';

import { usePost } from '../api/queries';
import { splitCaption } from '../publication/format';
import { usePublicationWindow } from '../publication/search';
import type { RouteBundle } from '../../routes/contract';
import { useCampaignOrganisationId, useOrgContext } from '../../session/orgContext';
import { Assistant, type AssistantScope, type PostScope } from './Assistant';
import { at } from './messages';
import { ASSISTANT_PATH } from './paths';
import { useAssistantPostId } from './search';

const CONTROL_PLANE_READ = 'control_plane.read';

function AssistantRoute(): ReactNode {
  const { campaignId } = useParams({ strict: false }) as Record<string, string | undefined>;
  const tenantId = useCampaignOrganisationId();
  const context = useOrgContext();
  const navigate = useNavigate();
  const postId = useAssistantPostId();
  // The post read requires a window for its figures, which this screen never
  // draws; any window resolves the same post.
  const window = usePublicationWindow();
  const postQuery = usePost(campaignId ?? '', tenantId ?? '', postId ?? '', { window });
  if (tenantId === null || context.kind !== 'campaign') return null;

  const postRow = postQuery.data?.post ?? null;
  const post: PostScope | null =
    postId === null || postRow === null
      ? null
      : {
          id: postId,
          // `handle` already carries its own `@` (fixtures.ts, PostCard.tsx).
          label: splitCaption(postRow.caption).heading ?? postRow.handle ?? postRow.post_id,
        };
  const unknownPost = postId !== null && postQuery.data !== undefined && postRow === null;

  const scope: AssistantScope = {
    campaignLabel: context.campaign.campaign_name,
    windowLabel: 'todo o período',
    window: null,
    post,
    unknownPost,
  };

  function clearPost(): void {
    void navigate({
      to: ASSISTANT_PATH,
      params: { campaignId: campaignId ?? '' },
      search: (prev: Record<string, unknown>) => {
        const rest = { ...prev };
        delete rest.post;
        return rest;
      },
    });
  }

  return createElement(Assistant, {
    campaignId: campaignId ?? '',
    tenantId,
    scope,
    onClearPost: clearPost,
  });
}

export const bundle: RouteBundle = {
  routes: [
    {
      path: ASSISTANT_PATH,
      element: createElement(AssistantRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'form',
    },
  ],
  nav: [],
  campaignNav: [
    {
      path: ASSISTANT_PATH,
      label: () => at('title'),
      requires: CONTROL_PLANE_READ,
      icon: Sparkles,
      order: 10,
      kind: 'form',
    },
  ],
};
